import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  startOfQuarter,
  endOfQuarter,
  eachMonthOfInterval,
  format,
  parse,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import Skeleton from './Skeleton';
import { Coins } from 'lucide-react';

interface RevenueEntry {
  id: string;
  date: string;
  cash_revenue: number;
  card_revenue: number;
  invoice_revenue: number;
}

export default function QuarterView() {
  const { year = '', quarter = '' } = useParams();
  const [monthlyTotals, setMonthlyTotals] = useState<Record<string, { cash: number; card: number; invoice: number; total: number; tips: number }>>({});
  const [loading, setLoading] = useState(true);
  const [showTips, setShowTips] = useState(false);

  // Calculate the start and end of the quarter
  const quarterDate = new Date(parseInt(year), (parseInt(quarter) - 1) * 3, 1);
  const quarterStart = startOfQuarter(quarterDate);
  const quarterEnd = endOfQuarter(quarterDate);
  const months = eachMonthOfInterval({ start: quarterStart, end: quarterEnd });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: settings } = await supabase
          .from('settings')
          .select('show_tips')
          .eq('user_id', user.id)
          .maybeSingle();

        if (settings) {
          setShowTips(settings.show_tips ?? true);
        }

        const { data, error } = await supabase
          .from('revenue_entries')
          .select('*')
          .gte('date', format(quarterStart, 'yyyy-MM-dd'))
          .lte('date', format(quarterEnd, 'yyyy-MM-dd'));

        if (!error && data) {
          const totals = data.reduce((acc, entry) => {
            const monthKey = entry.date.substring(0, 7); // YYYY-MM
            if (!acc[monthKey]) {
              acc[monthKey] = { cash: 0, card: 0, invoice: 0, total: 0, tips: 0 };
            }
            acc[monthKey].cash += entry.cash_revenue;
            acc[monthKey].card += entry.card_revenue;
            acc[monthKey].invoice += entry.invoice_revenue;
            acc[monthKey].total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue;
            return acc;
          }, {} as Record<string, { cash: number; card: number; invoice: number; total: number; tips: number }>);

          if (settings?.show_tips ?? true) {
            const { data: tipsData } = await supabase
              .from('hidden_revenue')
              .select('date, amount')
              .gte('date', format(quarterStart, 'yyyy-MM-dd'))
              .lte('date', format(quarterEnd, 'yyyy-MM-dd'));

            if (tipsData) {
              tipsData.forEach(tip => {
                const monthKey = tip.date.substring(0, 7);
                if (!totals[monthKey]) {
                  totals[monthKey] = { cash: 0, card: 0, invoice: 0, total: 0, tips: 0 };
                }
                totals[monthKey].tips += tip.amount;
              });
            }
          }

          setMonthlyTotals(totals);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [quarterStart, quarterEnd]);

  const quarterTotals = Object.values(monthlyTotals).reduce(
    (acc, month) => {
      acc.cash += month.cash;
      acc.card += month.card;
      acc.invoice += month.invoice;
      acc.total += month.total;
      acc.tips += month.tips;
      return acc;
    },
    { cash: 0, card: 0, invoice: 0, total: 0, tips: 0 }
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
        </div>

        {/* Table Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {[...Array(6)].map((_, i) => (
                    <th key={i} className="px-6 py-3">
                      <Skeleton className="h-4 w-24" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(3)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {[...Array(6)].map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <Skeleton className="h-6 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {[...Array(6)].map((_, i) => (
                    <td key={i} className="px-6 py-4">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Kwartaal {quarter}, {year}
        </h1>
        <button
          onClick={() => setShowTips(!showTips)}
          className={`w-full lg:w-auto flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors ${
            showTips
              ? 'bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Coins className="h-4 w-4 mr-2" />
          <span>Fooi {showTips ? 'verbergen' : 'tonen'}</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Maand
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Contant
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Pin
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Rekening
                </th>
                {showTips && (
                  <th className="px-6 py-4 text-right text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider">
                    Fooi
                  </th>
                )}
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Totaal
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {months.map((month) => {
                const monthKey = format(month, 'yyyy-MM');
                const totals = monthlyTotals[monthKey] || { cash: 0, card: 0, invoice: 0, total: 0, tips: 0 };

                return (
                  <tr key={monthKey} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {format(month, 'MMMM yyyy', { locale: nl })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.cash.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.card.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{totals.invoice.toFixed(2)}
                    </td>
                    {showTips && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-500 dark:text-red-400 font-medium">
                        €{totals.tips.toFixed(2)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-600 dark:text-blue-400">
                      €{(totals.total + totals.tips).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-800 dark:text-blue-200">
                  Kwartaaltotalen
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{quarterTotals.cash.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{quarterTotals.card.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{quarterTotals.invoice.toFixed(2)}
                </td>
                {showTips && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-red-500 dark:text-red-400">
                    €{quarterTotals.tips.toFixed(2)}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-800 dark:text-blue-200">
                  €{(quarterTotals.total + quarterTotals.tips).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}